import { useContext } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { LanguageContext } from "../App";
import { BOOKING_URL, CONTACT } from "../translations";

export default function Contact() {
  const { lang, t } = useContext(LanguageContext);
  const c = t.contact;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#2A2726] py-24 md:py-32 px-6"
    >
      <div className="max-w-7xl mx-auto">
        {/* CTA Block */}
        <div className="mb-20 md:mb-28 text-center max-w-2xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="font-['Manrope',sans-serif] text-xs uppercase tracking-[0.3em] text-white/40 mb-6"
            data-testid="contact-cta-overline"
          >
            {c.cta_overline}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, delay: 0.1 }}
            className="font-['Cormorant_Garamond',serif] text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-white tracking-tight mb-5"
            data-testid="contact-cta-headline"
          >
            {c.cta_headline}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-['Manrope',sans-serif] text-base text-white/50 leading-relaxed mb-10"
            data-testid="contact-cta-text"
          >
            {c.cta_text}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex flex-col items-center gap-5"
          >
            <a
              data-testid="contact-availability-btn"
              href={BOOKING_URL[lang]}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center bg-[#FDFBF7] text-[#2A2726] font-['Manrope',sans-serif] text-xs uppercase tracking-[0.25em] w-full sm:w-auto px-12 py-5 hover:bg-[#F5F1EA] transition-all duration-300"
            >
              {c.cta_button}
            </a>
            {/* Benefits + scarcity */}
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-1">
              {(t.trust_strip?.benefits || []).slice(0, 3).map((b, i) => (
                <span key={i} className="font-['Manrope',sans-serif] text-[10px] uppercase tracking-[0.2em] text-white/30">
                  ✓ {b}
                </span>
              ))}
            </div>
            <p className="font-['Manrope',sans-serif] text-[10px] uppercase tracking-[0.25em] text-[#7B907F]">
              {t.trust_strip?.scarcity}
            </p>
          </motion.div>
        </div>

        {/* Divider */}
        <div className="w-full h-px bg-white/10 mb-20 md:mb-24" />

        {/* Contacts grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
          {/* Left: details */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="font-['Manrope',sans-serif] text-xs uppercase tracking-[0.3em] text-white/40 mb-6"
              data-testid="contact-overline"
            >
              {c.contact_overline}
            </motion.p>
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-['Cormorant_Garamond',serif] text-3xl md:text-4xl font-light text-white mb-10"
              data-testid="contact-headline"
            >
              {c.contact_headline}
            </motion.h3>

            <div className="flex flex-col gap-6">
              <motion.a href={CONTACT.phoneHref} data-testid="contact-phone-link"
                initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
                className="flex items-center gap-4 text-white/70 hover:text-white transition-colors group">
                <div className="w-10 h-10 flex items-center justify-center border border-white/20 group-hover:border-white/60 transition-colors">
                  <Phone size={16} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-['Manrope',sans-serif] text-[10px] uppercase tracking-[0.2em] text-white/30 mb-0.5">{c.phone_label}</p>
                  <p className="font-['Manrope',sans-serif] text-sm">{CONTACT.phone}</p>
                </div>
              </motion.a>

              <motion.a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link"
                initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }}
                className="flex items-center gap-4 text-white/70 hover:text-white transition-colors group">
                <div className="w-10 h-10 flex items-center justify-center border border-white/20 group-hover:border-white/60 transition-colors">
                  <MessageCircle size={16} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-['Manrope',sans-serif] text-[10px] uppercase tracking-[0.2em] text-white/30 mb-0.5">{c.whatsapp_label}</p>
                  <p className="font-['Manrope',sans-serif] text-sm">{CONTACT.whatsapp}</p>
                </div>
              </motion.a>

              <motion.a href={CONTACT.emailHref} data-testid="contact-email-link"
                initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2 }}
                className="flex items-center gap-4 text-white/70 hover:text-white transition-colors group">
                <div className="w-10 h-10 flex items-center justify-center border border-white/20 group-hover:border-white/60 transition-colors">
                  <Mail size={16} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-['Manrope',sans-serif] text-[10px] uppercase tracking-[0.2em] text-white/30 mb-0.5">{c.email_label}</p>
                  <p className="font-['Manrope',sans-serif] text-sm">{CONTACT.email}</p>
                </div>
              </motion.a>

              <motion.div data-testid="contact-address"
                initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.3 }}
                className="flex items-start gap-4 text-white/70">
                <div className="w-10 h-10 flex items-center justify-center border border-white/20 flex-shrink-0 mt-0.5">
                  <MapPin size={16} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-['Manrope',sans-serif] text-[10px] uppercase tracking-[0.2em] text-white/30 mb-0.5">{c.address_label}</p>
                  <p className="font-['Manrope',sans-serif] text-sm leading-relaxed">{c.address}</p>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Right: WhatsApp CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="flex flex-col justify-center items-start md:items-center"
          >
            <div className="p-6 md:p-10 border border-white/10 text-center w-full max-w-sm">
              <div className="w-14 h-14 flex items-center justify-center border border-white/20 mx-auto mb-6">
                <MessageCircle size={22} strokeWidth={1.5} className="text-white/70" />
              </div>
              <p className="font-['Cormorant_Garamond',serif] text-2xl font-light text-white mb-2 leading-snug">
                {c.contact_headline}
              </p>
              <p className="font-['Manrope',sans-serif] text-xs text-white/40 tracking-[0.1em] mb-8">
                {CONTACT.whatsapp}
              </p>
              <a
                data-testid="contact-whatsapp-cta"
                href={CONTACT.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 w-full bg-[#25D366] text-white font-['Manrope',sans-serif] text-xs uppercase tracking-[0.2em] py-4 hover:bg-[#1fba59] transition-colors duration-300"
              >
                <MessageCircle size={15} />
                {c.whatsapp_cta}
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
